import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { toast } from "sonner";
import dayjs from "../../lib/dayjs";
import {
  createMedicalRecord,
  getMedicalRecord,
  updateMedicalRecord,
} from "../../services/medicalRecords.service";
import { getUserById } from "../../services/usersList.service";
import { generateMedicalRecordPDF } from "../../utils/generateMedicalRecordPDF";
import { generateOphthalmologyExamPDF } from "../../utils/generateOphthalmologyExamPDF";
import { generateSingleEyeExamPDF } from "../../utils/generateSingleEyeExamPDF";
import { generateClinicalDocumentPDF } from "../../utils/generateClinicalDocumentPDF";
import { FieldTemplateControls } from "./FieldTemplateControls";
import {
  ICreateMedicalRecordPayload,
  IMedicalRecord,
  IOphthalmologyRecord,
  IUpdateMedicalRecordPayload,
} from "../../interfaces/IMedicalRecord";
import type { IUser } from "../../interfaces";

// ─── tipos locais ──────────────────────────────────────────────────────────────

type OphFields = Omit<IOphthalmologyRecord, "id">;
type RecordFields = Omit<
  ICreateMedicalRecordPayload,
  "patientId" | "doctorId" | "appointmentId" | "cidCodes" | "insuranceCardNumber" | "ophthalmologyRecord"
>;

const EMPTY_OPH: OphFields = {
  visualAcuityOdSc: "", visualAcuityOdCc: "", visualAcuityOdPh: "",
  visualAcuityOeSc: "", visualAcuityOeCc: "", visualAcuityOePh: "",
  refractionOdEsf: "", refractionOdCil: "", refractionOdAxis: "", refractionOdAdd: "", refractionOdDnp: "",
  refractionOeEsf: "", refractionOeCil: "", refractionOeAxis: "", refractionOeAdd: "", refractionOeDnp: "",
  tonometryOd: "", tonometryOe: "",
  biomicroscopyOd: "", biomicroscopyOe: "",
  fundoscopyOd: "", fundoscopyOe: "",
  campimetryOd: "", campimetryOe: "",
  pachymetryOd: null, pachymetryOe: null,
  otherExams: "",
};

const EMPTY_FIELDS: RecordFields = {
  chiefComplaint: "", currentIllnessHistory: "",
  diagnosis: "", therapeuticPlan: "", prescription: "",
  insurancePlan: "", returnDate: "", observations: "",
};

function fromRecord(r: IMedicalRecord): { fields: RecordFields; oph: OphFields; cids: string[] } {
  return {
    fields: {
      chiefComplaint: r.chiefComplaint ?? "",
      currentIllnessHistory: r.currentIllnessHistory ?? "",
      diagnosis: r.diagnosis ?? "",
      therapeuticPlan: r.therapeuticPlan ?? "",
      prescription: r.prescription ?? "",
      insurancePlan: r.insurancePlan ?? "",
      returnDate: r.returnDate ? dayjs(r.returnDate).format("YYYY-MM-DD") : "",
      observations: r.observations ?? "",
    },
    oph: r.ophthalmologyRecord
      ? { ...EMPTY_OPH, ...r.ophthalmologyRecord, pachymetryOd: r.ophthalmologyRecord.pachymetryOd ?? null, pachymetryOe: r.ophthalmologyRecord.pachymetryOe ?? null }
      : { ...EMPTY_OPH },
    cids: r.cidCodes ?? [],
  };
}

// ─── helpers de UI ─────────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" fontWeight={700} color="#133859" sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
      {children}
    </Typography>
  );
}

function EyeField({ label, value, onChange, disabled, multiline }: {
  label: string; value: string; onChange: (v: string) => void; disabled: boolean; multiline?: boolean;
}) {
  return (
    <TextField label={label} size="small" fullWidth value={value}
      onChange={(e) => onChange(e.target.value)} disabled={disabled}
      multiline={multiline} minRows={multiline ? 2 : undefined} />
  );
}

// ─── componente principal ──────────────────────────────────────────────────────

export function MedicalRecord() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state: RootState) => state.userInfo);

  const isNew = !id || id === "novo";
  const patientIdParam = searchParams.get("patientId") ?? "";
  const doctorIdParam = searchParams.get("doctorId") ?? user?.doctor?.doctorId ?? "";
  const appointmentIdParam = searchParams.get("appointmentId") ?? "";

  const roleUpper = String(user?.role ?? "").toUpperCase();
  const isAdmin = roleUpper === "ADMIN" || roleUpper === "OWNER";
  const isDoctor = roleUpper === "DOCTOR" || roleUpper === "DOCTOR_OWNER";
  const isPatient = roleUpper === "USER";

  const [record, setRecord] = useState<IMedicalRecord | null>(null);
  const [patientData, setPatientData] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [printingExam, setPrintingExam] = useState(false);
  const [examPrintMenuAnchor, setExamPrintMenuAnchor] = useState<HTMLElement | null>(null);
  const [isEditing, setIsEditing] = useState(isNew);

  const [fields, setFields] = useState<RecordFields>({ ...EMPTY_FIELDS });
  const [oph, setOph] = useState<OphFields>({ ...EMPTY_OPH });
  const [cids, setCids] = useState<string[]>([]);
  const [cidInput, setCidInput] = useState("");

  const canEdit = isAdmin || (isDoctor && (isNew || record?.doctorId === user?.doctor?.doctorId));
  const canDownload = !isNew && record && (isAdmin || (isDoctor && record.doctorId === user?.doctor?.doctorId));
  const canPrintField = (value?: string | null) =>
    (isAdmin || (isDoctor && (isNew || record?.doctorId === user?.doctor?.doctorId))) &&
    (value ?? "").trim() !== "";
  const canPrintExam =
    (isAdmin || (isDoctor && (isNew || record?.doctorId === user?.doctor?.doctorId))) &&
    Object.values(oph).some((v) => v !== "" && v !== null && v !== undefined);

  // contexto compartilhado para os PDFs de exame/prescrição/etc.
  const pdfContext = {
    patientName: patientData?.name ?? record?.patient?.name ?? null,
    patientCPF: patientData?.profile?.registrationNumber ?? null,
    doctorName: record?.doctor?.user?.name ?? user?.name ?? null,
    doctorCRM: record?.doctor?.CRM ?? user?.doctor?.CRM ?? null,
    date: record?.createdAt ?? null,
  };

  const notEmpty = (v: string | number | null | undefined) => v !== "" && v !== null && v !== undefined;

  // cada item imprimível individualmente dentro de "Exame Oftalmológico"
  const EXAM_ITEMS = [
    {
      key: "acuidade",
      label: "Acuidade Visual",
      hasContent: () => [oph.visualAcuityOdSc, oph.visualAcuityOdCc, oph.visualAcuityOdPh, oph.visualAcuityOeSc, oph.visualAcuityOeCc, oph.visualAcuityOePh].some(notEmpty),
      print: () => generateSingleEyeExamPDF({
        examTitle: "Acuidade Visual",
        fieldLabels: ["SC", "CC", "P/H"],
        odValues: [oph.visualAcuityOdSc, oph.visualAcuityOdCc, oph.visualAcuityOdPh],
        oeValues: [oph.visualAcuityOeSc, oph.visualAcuityOeCc, oph.visualAcuityOePh],
        fileSlug: "acuidade-visual",
        ...pdfContext,
      }),
    },
    {
      key: "refracao",
      label: "Refração",
      hasContent: () => [oph.refractionOdEsf, oph.refractionOdCil, oph.refractionOdAxis, oph.refractionOdAdd, oph.refractionOdDnp, oph.refractionOeEsf, oph.refractionOeCil, oph.refractionOeAxis, oph.refractionOeAdd, oph.refractionOeDnp].some(notEmpty),
      print: () => generateSingleEyeExamPDF({
        examTitle: "Refração",
        fieldLabels: ["ESF", "CIL", "EIXO", "ADD", "DNP"],
        odValues: [oph.refractionOdEsf, oph.refractionOdCil, oph.refractionOdAxis, oph.refractionOdAdd, oph.refractionOdDnp],
        oeValues: [oph.refractionOeEsf, oph.refractionOeCil, oph.refractionOeAxis, oph.refractionOeAdd, oph.refractionOeDnp],
        fileSlug: "refracao",
        ...pdfContext,
      }),
    },
    {
      key: "tonometria",
      label: "Tonometria",
      hasContent: () => [oph.tonometryOd, oph.tonometryOe].some(notEmpty),
      print: () => generateSingleEyeExamPDF({
        examTitle: "Tonometria (mmHg)",
        fieldLabels: ["VALOR"],
        odValues: [oph.tonometryOd],
        oeValues: [oph.tonometryOe],
        fileSlug: "tonometria",
        ...pdfContext,
      }),
    },
    {
      key: "biomicroscopia",
      label: "Biomicroscopia",
      hasContent: () => [oph.biomicroscopyOd, oph.biomicroscopyOe].some(notEmpty),
      print: () => generateSingleEyeExamPDF({
        examTitle: "Biomicroscopia",
        fieldLabels: ["VALOR"],
        odValues: [oph.biomicroscopyOd],
        oeValues: [oph.biomicroscopyOe],
        fileSlug: "biomicroscopia",
        ...pdfContext,
      }),
    },
    {
      key: "fundoscopia",
      label: "Fundoscopia",
      hasContent: () => [oph.fundoscopyOd, oph.fundoscopyOe].some(notEmpty),
      print: () => generateSingleEyeExamPDF({
        examTitle: "Fundoscopia",
        fieldLabels: ["VALOR"],
        odValues: [oph.fundoscopyOd],
        oeValues: [oph.fundoscopyOe],
        fileSlug: "fundoscopia",
        ...pdfContext,
      }),
    },
    {
      key: "campimetria",
      label: "Campimetria",
      hasContent: () => [oph.campimetryOd, oph.campimetryOe].some(notEmpty),
      print: () => generateSingleEyeExamPDF({
        examTitle: "Campimetria",
        fieldLabels: ["VALOR"],
        odValues: [oph.campimetryOd],
        oeValues: [oph.campimetryOe],
        fileSlug: "campimetria",
        ...pdfContext,
      }),
    },
    {
      key: "paquimetria",
      label: "Paquimetria",
      hasContent: () => [oph.pachymetryOd, oph.pachymetryOe].some(notEmpty),
      print: () => generateSingleEyeExamPDF({
        examTitle: "Paquimetria (μm)",
        fieldLabels: ["VALOR"],
        odValues: [oph.pachymetryOd],
        oeValues: [oph.pachymetryOe],
        fileSlug: "paquimetria",
        ...pdfContext,
      }),
    },
    {
      key: "outros",
      label: "Outros Exames",
      hasContent: () => notEmpty(oph.otherExams),
      print: () => generateClinicalDocumentPDF({
        title: "OUTROS EXAMES",
        content: oph.otherExams ?? "",
        fileSlug: "outros-exames",
        ...pdfContext,
      }),
    },
  ];

  const handleDownloadPDF = async () => {
    if (!record) return;
    setDownloading(true);
    try {
      await generateMedicalRecordPDF(record, patientData);
    } catch {
      toast.error("Não foi possível gerar o PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const handlePrintExamItem = async (print: () => Promise<void>) => {
    setExamPrintMenuAnchor(null);
    setPrintingExam(true);
    try {
      await print();
    } catch {
      toast.error("Não foi possível gerar o PDF do exame.");
    } finally {
      setPrintingExam(false);
    }
  };

  const handlePrintAllExam = () => handlePrintExamItem(() => generateOphthalmologyExamPDF({
    exam: oph,
    ...pdfContext,
  }));

  // carrega dados do paciente para pré-preencher plano
  useEffect(() => {
    const pid = isNew ? patientIdParam : record?.patientId;
    if (!pid) return;
    let ignore = false;
    getUserById(pid).then((data) => {
      if (!ignore && data) {
        setPatientData(data);
        // pré-preenche plano somente ao criar
        if (isNew && data.profile?.programName) {
          setFields((prev) => ({ ...prev, insurancePlan: data.profile!.programName ?? "" }));
        }
      }
    }).catch(() => undefined);
    return () => { ignore = true; };
  }, [isNew, patientIdParam, record?.patientId]);

  // carrega registro existente
  useEffect(() => {
    if (isNew) return;
    let ignore = false;
    async function load() {
      try {
        const data = await getMedicalRecord(id!);
        if (ignore) return;
        setRecord(data);
        const parsed = fromRecord(data);
        setFields(parsed.fields);
        setOph(parsed.oph);
        setCids(parsed.cids);
      } catch {
        toast.error("Não foi possível carregar o prontuário.");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [id, isNew]);

  // handlers
  const setField = (k: keyof RecordFields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFields((prev) => ({ ...prev, [k]: e.target.value }));

  const setOphField = (k: keyof OphFields) => (v: string) =>
    setOph((prev) => ({ ...prev, [k]: v }));

  const setOphNum = (k: "pachymetryOd" | "pachymetryOe") => (v: string) =>
    setOph((prev) => ({ ...prev, [k]: v === "" ? null : Number(v) }));

  const addCid = () => {
    const v = cidInput.trim().toUpperCase();
    if (!v || cids.includes(v)) return;
    setCids((prev) => [...prev, v]);
    setCidInput("");
  };

  const buildOphPayload = (): Partial<IOphthalmologyRecord> => {
    const clean: any = {};
    Object.entries(oph).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) clean[k] = v; });
    return clean;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ophPayload = buildOphPayload();
      const returnDateISO = fields.returnDate ? new Date(fields.returnDate).toISOString() : undefined;

      if (isNew) {
        const payload: ICreateMedicalRecordPayload = {
          patientId: patientIdParam,
          doctorId: doctorIdParam,
          ...(appointmentIdParam ? { appointmentId: appointmentIdParam } : {}),
          ...fields,
          returnDate: returnDateISO,
          cidCodes: cids,
          ophthalmologyRecord: Object.keys(ophPayload).length ? ophPayload : undefined,
        };
        const created = await createMedicalRecord(payload);
        toast.success("Prontuário criado com sucesso.");
        navigate(`/prontuario/${created.id}`, { replace: true });
      } else {
        const payload: IUpdateMedicalRecordPayload = {
          ...fields,
          returnDate: returnDateISO,
          cidCodes: cids,
          ophthalmologyRecord: Object.keys(ophPayload).length ? ophPayload : undefined,
        };
        const updated = await updateMedicalRecord(id!, payload);
        setRecord(updated);
        const parsed = fromRecord(updated);
        setFields(parsed.fields);
        setOph(parsed.oph);
        setCids(parsed.cids);
        setIsEditing(false);
        toast.success("Prontuário atualizado com sucesso.");
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Não foi possível salvar o prontuário.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (record) {
      const parsed = fromRecord(record);
      setFields(parsed.fields);
      setOph(parsed.oph);
      setCids(parsed.cids);
    }
    setIsEditing(false);
  };

  const disabled = !isEditing;

  if (loading) {
    return (
      <Box sx={{ maxWidth: 960, mx: "auto", p: 4 }}>
        <Stack spacing={2}>{[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={80} />)}</Stack>
      </Box>
    );
  }

  // dados de exibição
  const patientName  = patientData?.name ?? record?.patient?.name ?? (isNew ? "—" : "—");
  const patientDob   = patientData?.profile?.birthDay ?? null;
  const patientPhone = patientData?.profile?.phone ?? null;
  const patientAddr  = [
    patientData?.profile?.address,
    patientData?.profile?.city,
    patientData?.profile?.state,
  ].filter(Boolean).join(", ") || null;

  const doctorName  = record?.doctor?.user?.name ?? user?.name ?? "—";
  const doctorCRM   = record?.doctor?.CRM ?? user?.doctor?.CRM ?? null;
  const recordDate  = record?.createdAt ? dayjs(record.createdAt).format("DD/MM/YYYY") : dayjs().format("DD/MM/YYYY");

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", px: { xs: 2, md: 4 }, py: 3 }}>

      {/* cabeçalho */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#133859">
            Prontuário Médico Oftalmológico
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isNew ? "Novo registro" : `Criado em ${recordDate}`}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          {canDownload && !isEditing && (
            <Button
              startIcon={downloading ? <CircularProgress size={14} /> : <DownloadIcon />}
              variant="outlined"
              size="small"
              onClick={handleDownloadPDF}
              disabled={downloading}
              sx={{ borderColor: "#133859", color: "#133859" }}
            >
              {downloading ? "Gerando..." : "Baixar PDF"}
            </Button>
          )}
          {!isNew && canEdit && !isEditing && (
            <Button startIcon={<EditIcon />} variant="outlined" size="small" onClick={() => setIsEditing(true)}>
              Editar
            </Button>
          )}
          {isEditing && (
            <>
              {!isNew && <Button size="small" color="inherit" onClick={handleCancelEdit} disabled={saving}>Cancelar</Button>}
              <Button
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                variant="contained" size="small" onClick={handleSave}
                disabled={saving} sx={{ backgroundColor: "#133859" }}
              >
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <Stack spacing={2}>

        {/* ── Identificação do Paciente ──────────────────────────────────────── */}
        <Box sx={{ p: 2, border: "1px solid #d0dce8", borderRadius: 2, backgroundColor: "#f0f6ff" }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <PersonOutlineIcon sx={{ color: "#133859", fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={700} color="#133859" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
              Identificação do Paciente
            </Typography>
          </Stack>

          <Grid container spacing={1.5}>

            {/* Nome — linha inteira */}
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary">Nome</Typography>
              <Typography fontWeight={600} color="#133859">{patientName}</Typography>
            </Grid>

            {/* Nascimento + Telefone + Data */}
            {patientDob && (
              <Grid size={{ xs: 6, sm: 4 }}>
                <Typography variant="caption" color="text.secondary">Nascimento</Typography>
                <Typography fontWeight={500}>{dayjs(patientDob).format("DD/MM/YYYY")}</Typography>
              </Grid>
            )}
            {patientPhone && (
              <Grid size={{ xs: 6, sm: 4 }}>
                <Typography variant="caption" color="text.secondary">Telefone</Typography>
                <Typography fontWeight={500}>{patientPhone}</Typography>
              </Grid>
            )}
            <Grid size={{ xs: 6, sm: 4 }}>
              <Typography variant="caption" color="text.secondary">Data da consulta</Typography>
              <Typography fontWeight={500}>{recordDate}</Typography>
            </Grid>

            {/* Endereço — linha inteira, sem truncamento */}
            {patientAddr && (
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">Endereço</Typography>
                <Typography fontWeight={500} sx={{ wordBreak: "break-word" }}>{patientAddr}</Typography>
              </Grid>
            )}

            {/* Separador visual */}
            <Grid size={12}>
              <Divider sx={{ borderColor: "#c5d8ea" }} />
            </Grid>

            {/* Médico e CRM em colunas independentes */}
            <Grid size={{ xs: 8, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Médico Responsável</Typography>
              <Typography fontWeight={600} color="#133859">{doctorName}</Typography>
            </Grid>
            {doctorCRM && (
              <Grid size={{ xs: 4, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">CRM</Typography>
                <Typography fontWeight={500}>{doctorCRM}</Typography>
              </Grid>
            )}

          </Grid>
        </Box>

        {/* ── Seção 1: Dados da Consulta ─────────────────────────────────────── */}
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#f0f4f8" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <EventNoteOutlinedIcon sx={{ color: "#133859", fontSize: 20 }} />
              <Typography fontWeight={600} color="#133859">Dados da Consulta</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField label="Queixa Principal" fullWidth multiline minRows={2}
                value={fields.chiefComplaint} onChange={setField("chiefComplaint")} disabled={disabled} />
              <TextField label="História da Moléstia Atual" fullWidth multiline minRows={3}
                value={fields.currentIllnessHistory} onChange={setField("currentIllnessHistory")} disabled={disabled} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Convênio / Plano" fullWidth size="small"
                    value={fields.insurancePlan} onChange={setField("insurancePlan")} disabled={disabled} />
                </Grid>
              </Grid>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* ── Seção 2: Exame Oftalmológico ───────────────────────────────────── */}
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#f0f4f8" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <VisibilityOutlinedIcon sx={{ color: "#133859", fontSize: 20 }} />
              <Typography fontWeight={600} color="#133859">Exame Oftalmológico</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* Acuidade Visual + Refração */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <SectionTitle>Acuidade Visual</SectionTitle>
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>OD</Typography>
                    <Grid container spacing={1}>
                      <Grid size={4}><EyeField label="SC" value={oph.visualAcuityOdSc ?? ""} onChange={setOphField("visualAcuityOdSc")} disabled={disabled} /></Grid>
                      <Grid size={4}><EyeField label="CC" value={oph.visualAcuityOdCc ?? ""} onChange={setOphField("visualAcuityOdCc")} disabled={disabled} /></Grid>
                      <Grid size={4}><EyeField label="P/H" value={oph.visualAcuityOdPh ?? ""} onChange={setOphField("visualAcuityOdPh")} disabled={disabled} /></Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>OE</Typography>
                    <Grid container spacing={1}>
                      <Grid size={4}><EyeField label="SC" value={oph.visualAcuityOeSc ?? ""} onChange={setOphField("visualAcuityOeSc")} disabled={disabled} /></Grid>
                      <Grid size={4}><EyeField label="CC" value={oph.visualAcuityOeCc ?? ""} onChange={setOphField("visualAcuityOeCc")} disabled={disabled} /></Grid>
                      <Grid size={4}><EyeField label="P/H" value={oph.visualAcuityOePh ?? ""} onChange={setOphField("visualAcuityOePh")} disabled={disabled} /></Grid>
                    </Grid>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                  <SectionTitle>Refração</SectionTitle>
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>OD</Typography>
                    <Grid container spacing={1}>
                      {(["refractionOdEsf","refractionOdCil","refractionOdAxis","refractionOdAdd","refractionOdDnp"] as const).map((k, i) => (
                        <Grid key={k} size="grow"><EyeField label={["ESF","CIL","EIXO","ADD","DNP"][i]} value={oph[k] ?? ""} onChange={setOphField(k)} disabled={disabled} /></Grid>
                      ))}
                    </Grid>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>OE</Typography>
                    <Grid container spacing={1}>
                      {(["refractionOeEsf","refractionOeCil","refractionOeAxis","refractionOeAdd","refractionOeDnp"] as const).map((k, i) => (
                        <Grid key={k} size="grow"><EyeField label={["ESF","CIL","EIXO","ADD","DNP"][i]} value={oph[k] ?? ""} onChange={setOphField(k)} disabled={disabled} /></Grid>
                      ))}
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>

              <Divider />

              {/* Tonometria + Biomicroscopia + Fundoscopia */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                  <SectionTitle>Tonometria (mmHg)</SectionTitle>
                  <Stack spacing={1}>
                    <EyeField label="OD" value={oph.tonometryOd ?? ""} onChange={setOphField("tonometryOd")} disabled={disabled} />
                    <EyeField label="OE" value={oph.tonometryOe ?? ""} onChange={setOphField("tonometryOe")} disabled={disabled} />
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 4, md: 5 }}>
                  <SectionTitle>Biomicroscopia</SectionTitle>
                  <Stack spacing={1}>
                    <EyeField label="OD" value={oph.biomicroscopyOd ?? ""} onChange={setOphField("biomicroscopyOd")} disabled={disabled} multiline />
                    <EyeField label="OE" value={oph.biomicroscopyOe ?? ""} onChange={setOphField("biomicroscopyOe")} disabled={disabled} multiline />
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 4, md: 5 }}>
                  <SectionTitle>Fundoscopia</SectionTitle>
                  <Stack spacing={1}>
                    <EyeField label="OD" value={oph.fundoscopyOd ?? ""} onChange={setOphField("fundoscopyOd")} disabled={disabled} multiline />
                    <EyeField label="OE" value={oph.fundoscopyOe ?? ""} onChange={setOphField("fundoscopyOe")} disabled={disabled} multiline />
                  </Stack>
                </Grid>
              </Grid>

              <Divider />

              {/* Campimetria + Paquimetria + Outros */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <SectionTitle>Campimetria</SectionTitle>
                  <Stack spacing={1}>
                    <EyeField label="OD" value={oph.campimetryOd ?? ""} onChange={setOphField("campimetryOd")} disabled={disabled} />
                    <EyeField label="OE" value={oph.campimetryOe ?? ""} onChange={setOphField("campimetryOe")} disabled={disabled} />
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <SectionTitle>Paquimetria (μm)</SectionTitle>
                  <Stack spacing={1}>
                    <TextField label="OD" size="small" fullWidth type="number" value={oph.pachymetryOd ?? ""} onChange={(e) => setOphNum("pachymetryOd")(e.target.value)} disabled={disabled} />
                    <TextField label="OE" size="small" fullWidth type="number" value={oph.pachymetryOe ?? ""} onChange={(e) => setOphNum("pachymetryOe")(e.target.value)} disabled={disabled} />
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <SectionTitle>Outros Exames</SectionTitle>
                  <TextField fullWidth multiline minRows={3} size="small" value={oph.otherExams ?? ""} onChange={(e) => setOphField("otherExams")(e.target.value)} disabled={disabled} />
                </Grid>
              </Grid>

              {canPrintExam && (
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    startIcon={printingExam ? <CircularProgress size={14} /> : <LocalPrintshopOutlinedIcon />}
                    variant="outlined"
                    size="small"
                    onClick={(e) => setExamPrintMenuAnchor(e.currentTarget)}
                    disabled={printingExam}
                    sx={{ borderColor: "#133859", color: "#133859" }}
                  >
                    {printingExam ? "Gerando..." : "Imprimir"}
                  </Button>
                  <Menu
                    anchorEl={examPrintMenuAnchor}
                    open={Boolean(examPrintMenuAnchor)}
                    onClose={() => setExamPrintMenuAnchor(null)}
                  >
                    {EXAM_ITEMS.map((item) => (
                      <MenuItem
                        key={item.key}
                        disabled={!item.hasContent()}
                        onClick={() => handlePrintExamItem(item.print)}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem onClick={handlePrintAllExam}>
                      Imprimir tudo
                    </MenuItem>
                  </Menu>
                </Stack>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* ── Seção 3: Avaliação e Conduta ───────────────────────────────────── */}
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#f0f4f8" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <MedicalServicesOutlinedIcon sx={{ color: "#133859", fontSize: 20 }} />
              <Typography fontWeight={600} color="#133859">Avaliação e Conduta</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Box>
                <TextField label="Hipóteses Diagnósticas" fullWidth multiline minRows={2}
                  value={fields.diagnosis} onChange={setField("diagnosis")} disabled={disabled} />
                <FieldTemplateControls
                  fieldType="DIAGNOSIS"
                  value={fields.diagnosis ?? ""}
                  onApply={(content) => setFields((prev) => ({ ...prev, diagnosis: content }))}
                  editable={!disabled}
                  isDoctor={isDoctor}
                  canPrint={canPrintField(fields.diagnosis)}
                  printLabel="Imprimir Hipóteses Diagnósticas"
                  printTitle="HIPÓTESES DIAGNÓSTICAS"
                  printFileSlug="diagnostico"
                  {...pdfContext}
                />
              </Box>

              {/* CID */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Códigos CID</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  {cids.map((c) => (
                    <Chip key={c} label={c} size="small" onDelete={disabled ? undefined : () => setCids((prev) => prev.filter((x) => x !== c))}
                      color="primary" variant="outlined" />
                  ))}
                </Stack>
                {!disabled && (
                  <Stack direction="row" spacing={1} sx={{ maxWidth: 260 }}>
                    <TextField size="small" placeholder="Ex: H40.1" value={cidInput}
                      onChange={(e) => setCidInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addCid()}
                      inputProps={{ maxLength: 8 }} />
                    <IconButton size="small" onClick={addCid} sx={{ color: "#133859" }}><AddIcon /></IconButton>
                  </Stack>
                )}
              </Box>

              <Box>
                <TextField label="Conduta / Plano Terapêutico" fullWidth multiline minRows={3}
                  value={fields.therapeuticPlan} onChange={setField("therapeuticPlan")} disabled={disabled} />
                <FieldTemplateControls
                  fieldType="THERAPEUTIC_PLAN"
                  value={fields.therapeuticPlan ?? ""}
                  onApply={(content) => setFields((prev) => ({ ...prev, therapeuticPlan: content }))}
                  editable={!disabled}
                  isDoctor={isDoctor}
                  canPrint={canPrintField(fields.therapeuticPlan)}
                  printLabel="Imprimir Conduta"
                  printTitle="CONDUTA / PLANO TERAPÊUTICO"
                  printFileSlug="conduta"
                  {...pdfContext}
                />
              </Box>

              <Box>
                <TextField label="Prescrição" fullWidth multiline minRows={3}
                  value={fields.prescription} onChange={setField("prescription")} disabled={disabled} />
                <FieldTemplateControls
                  fieldType="PRESCRIPTION"
                  value={fields.prescription ?? ""}
                  onApply={(content) => setFields((prev) => ({ ...prev, prescription: content }))}
                  editable={!disabled}
                  isDoctor={isDoctor}
                  canPrint={canPrintField(fields.prescription)}
                  printLabel="Imprimir Prescrição"
                  printTitle="RECEITUÁRIO MÉDICO"
                  printFileSlug="receita"
                  printUppercase
                  {...pdfContext}
                />
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* ── Seção 4: Retorno ───────────────────────────────────────────────── */}
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#f0f4f8" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <EventRepeatOutlinedIcon sx={{ color: "#133859", fontSize: 20 }} />
              <Typography fontWeight={600} color="#133859">Retorno</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField label="Data do Retorno" type="date" fullWidth size="small"
                    InputLabelProps={{ shrink: true }} value={fields.returnDate}
                    onChange={setField("returnDate")} disabled={disabled} />
                </Grid>
              </Grid>
              <TextField label="Observações" fullWidth multiline minRows={3}
                value={fields.observations} onChange={setField("observations")} disabled={disabled} />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* botão de salvar no rodapé */}
        {isEditing && (
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ pt: 1 }}>
            {!isNew && <Button color="inherit" onClick={handleCancelEdit} disabled={saving}>Cancelar</Button>}
            <Button
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              variant="contained" onClick={handleSave}
              disabled={saving || (!isNew && !canEdit)}
              sx={{ backgroundColor: "#133859" }}
            >
              {saving ? "Salvando..." : "Salvar prontuário"}
            </Button>
          </Stack>
        )}

        {isPatient && (
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            Visualização somente leitura.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
